import { Paper, IconButton, Tooltip, useTheme, Chip, Box, Grid, Typography } from '@mui/material';
import { Edit2, Trash2, Mail, Phone, Clock } from 'lucide-react';
import { Resource } from '../../types/resource';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';

interface ResourceCardProps {
  resources: Resource[];
  onEdit: (resource: Resource) => void;
  onDelete: (id: number) => void;
  onSelect: (resource: Resource) => void;
}

export default function ResourceCard({ resources, onEdit, onDelete, onSelect }: ResourceCardProps) {
  const theme = useTheme();
  const { role } = useAuth();
  return (
    <>
      <Grid container spacing={2}>
        {resources.map(resource => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            key={resource.id}
            sx={{ cursor: 'pointer' }}
            onClick={() => onSelect(resource)}
          >
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
                height: '100%',
                py: 2,
                px: 2.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly',
                gap: 1.5,
              }}
              component={Paper}
              elevation={0}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {resource.name}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Mail size={14} />
                  {resource.email}
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Phone size={14} />
                  {resource.phone}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Services
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {resource.serviceResourceMapping?.map(({ services }) => (
                    <Chip
                      key={services.id}
                      label={services.name}
                      size="small"
                      sx={{
                        backgroundColor: `${theme.palette.primary.main}10`,
                        color: theme.palette.primary.main,
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}
                >
                  <Clock size={14} />
                  {resource.timezone}
                </Typography>

                {role !== AUTH_CONSTANTS.ROLES.USER ? (
                  <Box sx={{}}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          onEdit(resource);
                        }}
                        sx={{
                          mr: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 100,
                        }}
                      >
                        <Edit2 size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={e => {
                          e.stopPropagation();
                          onDelete(resource.id);
                        }}
                        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 100 }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </>
  );
}
